import React, { FC } from "react";
import { Popover } from "antd";
import { Colors } from "../colors";
import { Gpm as GpmDataType, GpmType, isRangeGpm, GpmThreshold } from "../../data-types";
import { Gpm } from "../matter-editor/gpm";
import { AddPricingTooltip } from "./add-pricing-slider-tooltip";
import { InfoCircleOutlined } from "@ant-design/icons";

import styles from "./gpm-slider.module.css";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

const { ["slider-indicator"]: sliderIndicatorClassName } = styles as any;

export type GpmSliderProps = {
  showGpm: boolean;
  gpmThreshold: GpmThreshold;
  gpm: GpmDataType | number;
  timeEntryCount?: number;
  isFeeEarnerLevelGpm: boolean;
  totalWidth?: number;
  barHeight?: number;
};

type indicatorProps = {
  position: number;
  status: "red" | "amber" | "green";
};

function positive(x: number) {
  return x > 0 ? x : 0;
}

const ensureOrder = (a: number, b: number) => (a > b ? [b, a] : [a, b]);

const statusColors = {
  red: Colors.Error,
  amber: Colors.Warning,
  green: Colors.Success,
};

export const indicatorPropsFromGpm = (
  gpm: number,
  gpmTarget: number,
  warningThreshold: number
): indicatorProps => {
  if (gpm < 0) {
    return {
      position: 0,
      status: "red",
    };
  }
  // amber percent is a percentage of the gpmTarget. e.g. if target is 70% and warning threshold is 10% then amberPercent is 7%.
  const amberPercent = (warningThreshold * gpmTarget) / 100;
  const redPercent = gpmTarget - amberPercent;
  let position =
    Math.min(gpm / redPercent, 1) +
    Math.min(positive(gpm - redPercent) / amberPercent, 1) +
    Math.min(positive(gpm - redPercent - amberPercent) / (100 - redPercent - amberPercent), 1);
  position *= 100 / 3;
  const status = gpm < redPercent ? "red" : gpm < redPercent + amberPercent ? "amber" : "green";
  return {
    position: position,
    status: status,
  };
};

function isGpmDataType(gpm: any): gpm is GpmDataType {
  return gpm && (typeof gpm.fixed === "number" || typeof gpm.lower === "number");
}

export const GpmSlider: FC<GpmSliderProps> = ({
  showGpm,
  gpmThreshold,
  gpm: gpmRaw,
  timeEntryCount,
  isFeeEarnerLevelGpm,
  totalWidth = 200,
  barHeight = 20,
}) => {
  const taxonomy = useTaxonomyLabel();

  const FE_LABEL = taxonomy.FeeEarner;

  const gpmTarget = gpmThreshold.gpmDangerThreshold;
  const warningThreshold = gpmThreshold.gpmWarningThreshold;

  if (gpmRaw === null || !warningThreshold || !gpmTarget) {
    return <AddPricingTooltip>Not Available</AddPricingTooltip>;
  }

  if (timeEntryCount === 0) {
    return (
      <span>
        <InfoCircleOutlined style={{ marginRight: 5 }} />
        No time entries
      </span>
    );
  }

  const gpm: GpmDataType = isGpmDataType(gpmRaw) ? gpmRaw : { type: GpmType.Fixed, fixed: gpmRaw };

  const isRange = isGpmDataType(gpm) ? isRangeGpm(gpm) : false;

  const [rawLower, rawUpper] = isRangeGpm(gpm) ? [gpm.lower, gpm.upper] : [gpm.fixed, null];
  const [gpmLower, gpmUpper] = rawUpper ? ensureOrder(rawLower, rawUpper) : [rawLower, null];

  const indicatorLower = indicatorPropsFromGpm(gpmLower, gpmTarget, warningThreshold);
  const indicatorUpper = gpmUpper
    ? indicatorPropsFromGpm(gpmUpper, gpmTarget, warningThreshold)
    : null;

  const statusText: () => React.ReactNode = () => {
    if (showGpm) {
      return (
        <Gpm
          isFeeEarnerLevelGpm={isFeeEarnerLevelGpm}
          gpm={gpm}
          gpmThreshold={gpmThreshold}
          showRequired={false}
          showLabel={true}
          decimalPlaces={2}
        />
      );
    } else if ((indicatorUpper && indicatorLower.status === indicatorUpper.status) || !isRange) {
      return (
        <div>
          GPM {isRange ? "range" : ""} is in the{" "}
          <b style={{ color: statusColors[indicatorLower.status] }}>{indicatorLower.status}</b>
        </div>
      );
    } else {
      return (
        <div>
          Lower GPM bound is in the{" "}
          <b style={{ color: statusColors[indicatorLower.status] }}>{indicatorLower.status}</b>
        </div>
      );
    }
  };

  return (
    <Popover
      content={
        <div style={{ maxWidth: "200px", textAlign: "center" }}>
          {statusText()}
          {isFeeEarnerLevelGpm && (
            <div>
              <br></br>
              <p>
                This is an approximate GPM due to this proposal using {taxonomy.FeeEarnerLevel}s and
                not specific {FE_LABEL}s.
              </p>
            </div>
          )}
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          width: `${totalWidth}px`,
          height: `${barHeight}px`,
          position: "relative",
          overflow: "visible",
        }}
      >
        <div
          style={{
            width: `${totalWidth / 3}px`,
            background: statusColors.red,
            top: "5px",
            position: "relative",
            borderTopLeftRadius: "2px",
            borderBottomLeftRadius: "2px",
          }}
        />
        <div
          style={{
            width: `${totalWidth / 3}px`,
            background: statusColors.amber,
            top: "5px",
            position: "relative",
          }}
        />
        <div
          style={{
            width: `${totalWidth / 3}px`,
            background: statusColors.green,
            top: "5px",
            position: "relative",
            borderTopRightRadius: "2px",
            borderBottomRightRadius: "2px",
          }}
        />
        <div
          className={sliderIndicatorClassName}
          style={{ left: `${indicatorLower.position}%`, top: "5px", height: `${barHeight}px` }}
        />
        {indicatorUpper && (
          <>
            <div
              className={sliderIndicatorClassName}
              style={{ left: `${indicatorUpper.position}%`, top: "5px", height: `${barHeight}px` }}
            />
            <div
              style={{
                position: "absolute",
                top: "5px",
                left: `${indicatorLower.position}%`,
                width: `${indicatorUpper.position - indicatorLower.position}%`,
                height: `${barHeight}px`,
                background:
                  "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.1) 4px)",
              }}
            />
          </>
        )}
      </div>
    </Popover>
  );
};
